import React, { useCallback, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { useStores } from "../../hooks/useStores";
import { Observer, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/Container";
import clientsConfig from "../../config/clients";
import { SearchBar } from "../../components/SearchBar";
import {
  Box,
  Flex,
  Spacer,
  Center,
  useDisclosure,
  useToast,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Link as CLink,
} from "@chakra-ui/react";
import { CustomerModal } from "../../components/CustomerModal";
import { useEffect } from "react";
import { Subscriptions, SubscriptionStatus, User } from "../../types";
import { PrimayButton } from "../../components/Button";
import useDebounce from "../../hooks/useDebounce";
import { useMemo } from "react";
import { fuzzyMatch } from "../../utils/string";
import { VirtualizedTable } from "../../components/VirtualizedTable";
import { Select } from "../../components/Select";
import moment from "moment";
import { isTrialExpired } from "../../utils/trial";
import dayjs from "dayjs";
import api from "../../services/api";
import {
  getSubscriptionType,
  getSubscriptionStatus,
} from "../../utils/subscription";

export const Clients: React.FC = observer(() => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<
    "ASC" | "DESC" | undefined
  >(undefined);
  const debouncedFilters = useDebounce(clientsStore.clientsFilters, 300);

  useEffect(() => {
    clientsStore.getClients();
  }, []);

  const handleSort = useCallback(
    (dataKey: string) => {
      if (dataKey === sortBy && sortDirection === "ASC") {
        setSortDirection("DESC");
      } else if (dataKey === sortBy && sortDirection === "DESC") {
        setSortBy(undefined);
        setSortDirection(undefined);
      } else {
        setSortBy(dataKey);
        setSortDirection("ASC");
      }
    },
    [sortBy, sortDirection, setSortBy, setSortDirection]
  );

  const filteredCustomers = useMemo(() => {
    return debouncedQuery.length > 1
      ? clientsStore.clients.filter((client) =>
          fuzzyMatch(JSON.stringify(client), debouncedQuery)
        )
      : clientsStore.clients;
  }, [clientsStore.clients, debouncedQuery]);

  const handleSubmit = useCallback(
    async (data: Partial<User>) => {
      try {
        await clientsStore.addCustomer(data);
        onClose();
        toast({
          status: "success",
          title: t("common.actionExecutedSuccessfully"),
        });
      } catch (err) {
        toast({
          status: "error",
          title: t("common.error"),
        });
      }
      clientsStore.getClients();
    },
    [clientsStore, onClose, t, toast]
  );

  const changeFilter = useCallback(
    (dataKey, value) => {
      clientsStore.updateProjectFilters(dataKey, value);
    },
    [clientsStore]
  );

  const getTextSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: () => (
        <Observer>
          {() => (
            <FormControl>
              <InputGroup>
                <Input
                  id={dataIndex}
                  value={
                    (clientsStore.clientsFilters[dataIndex] as string) || ""
                  }
                  onChange={(e) => changeFilter(dataIndex, e.target.value)}
                  placeholder={`${t("common.search")} ${t(
                    `screens.clients.${dataIndex}`
                  )}`}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={(e) => changeFilter(dataIndex, "")}
                  >
                    {"Reset"}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Observer>
      ),
    }),
    [changeFilter, clientsStore, t]
  );

  const susbscriptionOptions = useMemo(
    () =>
      clientsConfig.subscriptions.map((subscription) => ({
        label: subscription,
        value: subscription,
      })),
    []
  );

  const getSelectedSubscriptions: any = useCallback(
    (subscriptionList: Subscriptions[]) => {
      return (
        subscriptionList &&
        subscriptionList.map((subscription) => ({
          value: subscription,
          label: subscription,
        }))
      );
    },
    []
  );

  const getSubscriptionSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: () => (
        <Observer>
          {() => (
            <FormControl>
              <InputGroup>
                <div style={{ width: "100%" }}>
                  <Select
                    id={dataIndex}
                    options={susbscriptionOptions}
                    // @ts-ignore
                    isMulti
                    value={
                      getSelectedSubscriptions(
                        clientsStore.clientsFilters[dataIndex]
                      ) || []
                    }
                    onChange={(options: any) => {
                      const selectedOptions = options as any;

                      changeFilter(
                        dataIndex,
                        options
                          ? (
                              selectedOptions as {
                                label: string;
                                value: string;
                              }[]
                            ).map((option) => option.value)
                          : null || null
                      );
                    }}
                  />
                </div>
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={(e) => changeFilter(dataIndex, "")}
                  >
                    {"Reset"}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Observer>
      ),
    }),
    [susbscriptionOptions, getSelectedSubscriptions, clientsStore, changeFilter]
  );

  const statusOptions = useMemo(
    () =>
      clientsConfig.status.map((status) => ({
        label: t(
          `screens.client.customerTableHead.subscriptionState.${status}`
        ),
        value: t(
          `screens.client.customerTableHead.subscriptionState.${status}`
        ),
      })),
    [t]
  );

  const getSelectedStatus: any = useCallback(
    (statusList: SubscriptionStatus[]) => {
      return (
        statusList &&
        statusList.map((status) => ({
          value: status,
          label: status,
        }))
      );
    },
    []
  );

  const getSubscriptionStatusSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: () => (
        <Observer>
          {() => (
            <FormControl>
              <InputGroup>
                <div style={{ width: "100%" }}>
                  <Select
                    id={dataIndex}
                    options={statusOptions}
                    // @ts-ignore
                    isMulti
                    value={
                      getSelectedStatus(
                        clientsStore.clientsFilters[dataIndex]
                      ) || []
                    }
                    onChange={(options: any) => {
                      const selectedOptions = options as any;

                      changeFilter(
                        dataIndex,
                        options
                          ? (
                              selectedOptions as {
                                label: string;
                                value: string;
                              }[]
                            ).map((option) => option.value)
                          : null || null
                      );
                    }}
                  />
                </div>
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={(e) => changeFilter(dataIndex, "")}
                  >
                    {"Reset"}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Observer>
      ),
    }),
    [changeFilter, getSelectedStatus, clientsStore, statusOptions]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.surname"),
        dataKey: "surname",
        sorter: (a: { surname: string }, b: { surname: any }) =>
          a.surname.localeCompare(b.surname),
        render: (surname: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {surname || "-"}
            </CLink>
          );
        },
        ...getTextSearchProps("surname"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.name"),
        dataKey: "name",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {name || "-"}
            </CLink>
          );
        },
        ...getTextSearchProps("name"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.email"),
        dataKey: "email",
        sorter: (a: { email: string }, b: { email: any }) =>
          a.email.localeCompare(b.email),
        ...getTextSearchProps("email"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.registrationDate"),
        dataKey: "createdAt",
        sorter: (
          a: { createdAt: moment.MomentInput },
          b: { createdAt: moment.MomentInput }
        ) => moment.utc(a.createdAt).diff(moment.utc(b.createdAt)),
        render: (createdAt: string) => {
          return moment(createdAt).format("DD/MM/YYYY");
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: "Abbonamento",
        dataKey: "subscription",
        sorter: (a: any, b: any) => {
          return a.subscription.localeCompare(b.subscription);
        },
        render: (subscription_status: User) => {
          return subscription_status;
        },
        ...getSubscriptionSearchProps("subscription"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: "Stato abbonamento",
        dataKey: "statusSubscription",
        sorter: (a: any, b: any) => {
          return a.statusSubscription.localeCompare(b.statusSubscription);
        },
        ...getSubscriptionStatusSearchProps("statusSubscription"),
      },
    ],
    [
      getSubscriptionStatusSearchProps,
      getSubscriptionSearchProps,
      getTextSearchProps,
      t,
    ]
  );

  const customers = useMemo(() => {
    let clients = [...clientsStore.clients];

    clients = clients.map((customer) => {
      const data = { ...customer };

      // @ts-ignore
      data.subscription = getSubscriptionType(customer.subscription_type);
      // @ts-ignore
      data.statusSubscription = getSubscriptionStatus(customer);

      return data;
    });

    if (sortBy) {
      const columnData = columns.find((column) => column.dataKey === sortBy);

      if (columnData?.sorter) {
        clients = clients.sort(columnData?.sorter);

        if (sortDirection === "DESC") {
          clients = clients.reverse();
        }
      }
    }

    if (debouncedQuery) {
      clients = clients.filter(
        (client) =>
          !debouncedQuery || fuzzyMatch(JSON.stringify(client), debouncedQuery)
      );
    }

    for (let dataKey of Object.keys(debouncedFilters)) {
      clients = clients.filter((client: any) =>
        Array.isArray(debouncedFilters[dataKey])
          ? (debouncedFilters[dataKey] as string[]).some((searchQuery) =>
              ["statusSubscription"].includes(dataKey)
                ? client[dataKey] === searchQuery
                : fuzzyMatch(JSON.stringify(client[dataKey]), searchQuery)
            )
          : fuzzyMatch(
              JSON.stringify(client[dataKey]),
              debouncedFilters[dataKey] as string
            )
      );
    }

    return clients;
  }, [
    clientsStore.clients,
    sortBy,
    debouncedQuery,
    columns,
    sortDirection,
    debouncedFilters,
  ]);

  const handleDownloadReport = useCallback(async () => {
    const rows = [
      [
        t("screens.client.customerTableHead.surname").toUpperCase(),
        t("screens.client.customerTableHead.name").toUpperCase(),
        t("screens.client.customerTableHead.email").toUpperCase(),
        t("screens.client.customerTableHead.registrationDate").toUpperCase(),
        "ABBONAMENTO",
        "STATO ABBONAMENTO",
      ],
    ];

    for (let customer of clientsStore.clients) {
      rows.push([
        customer.surname,
        customer.name,
        customer.email,
        moment(customer.createdAt).format("DD/MM/YYYY"),
        getSubscriptionType(customer.subscription_type || ""),
        getSubscriptionStatus(customer),
      ]);
    }

    const blob = await api.generateExportFile(rows);

    if (blob) {
      //  Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${dayjs().format("DD-MM-YYYY")}_export_clienti.xlsx`
      );

      //  Append to html page
      document.body.appendChild(link);
      // Force download
      link.click();
      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    }
  }, [clientsStore.clients, t]);

  return (
    <Box p={30} w={"100%"}>
      <Flex mb={"20px"}>
        <Center p="4">{t("screens.clients.title")}</Center>
        <Spacer />
        <Box p="4">
          <Button
            colorScheme={"purple"}
            variant={"ghost"}
            onClick={handleDownloadReport}
          >
            Scarica report
          </Button>
          <PrimayButton onClick={onOpen}>
            {t("screens.clients.newButton")}
          </PrimayButton>
        </Box>
      </Flex>

      <Container>
        <SearchBar
          placeholder="Ricerca..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <div
          style={{
            flex: "1 1 auto",
            width: "100%",
            height: `${(filteredCustomers.length + 1) * 50}px`,
            display: "flex",
            marginTop: "20px",
          }}
        >
          <VirtualizedTable
            rowCount={customers.length}
            rowGetter={({ index }) => customers[index]}
            columns={columns}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onHeaderCellClick={handleSort}
            activeFilters={clientsStore.clientsFilters}
            rowStyle={(row) =>
              row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
            }
          />
        </div>
      </Container>
      <CustomerModal
        title={t("screens.client.clientModal.typeNew")}
        isSubmitting={clientsStore.isSubmitting}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
      />
    </Box>
  );
});
